/**
 * EPMS demo harness — the Angular perio chart's DOM contract, enough for
 * the real Dock adapter (keystroke writer + miss sync) to run against it:
 * two arch panels with tooth header buttons (`id` = tooth − 1), PD value
 * cells that flip span ⇄ input on Edit, and the Miss/Present host
 * behaviour transcribed from docs/pms/epms.md §14.3, §60, §62.
 *
 * Test scaffolding only. Nothing in `src/` imports from `demo/`. Live
 * HUD / writer belong to the unpacked Dock content script.
 */

import {
  addressEpmsPdCell,
  listEpmsToothColumnCellIds,
  type EpmsPdSection,
} from '../../src/pms/adapters/epms/grid-address';
import type { LivePerioSite } from '../../src/pms/live-perio/ports';

const FACIAL_SITES: readonly LivePerioSite[] = ['MB', 'B', 'DB'];
const LINGUAL_SITES: readonly LivePerioSite[] = ['ML', 'L', 'DL'];

/** EPMS enters Edit in ~0.5–6 s; raise this to force the SCRIBE-1136 race. */
export const DEFAULT_EDIT_DELAY_MS = 300;

interface ArchSpec {
  panelId: 'perioUpperPanel' | 'perioLowerPanel';
  panelClass: 'upperPanel' | 'lowerPanel';
  /** Row order top → bottom; the tooth header row sits between them (§7.2). */
  above: EpmsPdSection;
  below: EpmsPdSection;
}

const ARCHES: readonly ArchSpec[] = [
  { panelId: 'perioUpperPanel', panelClass: 'upperPanel', above: 'fUpper', below: 'lUpper' },
  { panelId: 'perioLowerPanel', panelClass: 'lowerPanel', above: 'lLower', below: 'fLower' },
];

function sitesFor(section: EpmsPdSection): readonly LivePerioSite[] {
  return section.startsWith('f') ? FACIAL_SITES : LINGUAL_SITES;
}

function toothNumber(section: EpmsPdSection, toothIndex: number): number {
  return section.includes('Upper') ? toothIndex + 1 : 32 - toothIndex;
}

/** Angular's keyup handler stand-in: one digit sets the value, Backspace clears. */
function onPerioKeyup(this: HTMLInputElement, event: KeyboardEvent): void {
  if (event.code === 'Backspace' || event.code === 'Delete') {
    this.value = '';
    return;
  }
  if (/^\d$/.test(event.key)) this.value = event.key;
}

function mountValue(
  doc: Document,
  valueId: string,
  title: string,
  edit: boolean,
  text = '',
): HTMLElement {
  const el = doc.createElement(edit ? 'input' : 'span');
  el.id = valueId;
  el.className = 'inputs mob-focus perio-chart-input inputClrBlack';
  el.setAttribute('title', title);
  if (el instanceof HTMLInputElement) {
    el.type = 'number';
    el.value = text;
    el.addEventListener('keyup', onPerioKeyup);
  } else {
    el.textContent = text;
  }
  return el;
}

function mountPdRow(doc: Document, section: EpmsPdSection): HTMLTableRowElement {
  const row = doc.createElement('tr');
  row.setAttribute('data-epms-section', section);
  for (let i = 0; i < 16; i++) {
    const td = doc.createElement('td');
    td.id = `${section}Pds${i}`;
    const tooth = toothNumber(section, i);
    for (const site of sitesFor(section)) {
      const addr = addressEpmsPdCell(tooth, site);
      if (!addr) continue;
      td.appendChild(mountValue(doc, addr.valueId, `#${tooth} ${site}`, false));
    }
    row.appendChild(td);
  }
  return row;
}

function mountHeaderRow(doc: Document, section: EpmsPdSection): HTMLTableRowElement {
  const row = doc.createElement('tr');
  row.className = 'epms-tooth-headers';
  for (let i = 0; i < 16; i++) {
    const th = doc.createElement('th');
    const tooth = toothNumber(section, i);
    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn-primary btn-sm btn-flat btn-exp';
    btn.id = String(tooth - 1);
    btn.textContent = String(tooth);
    btn.disabled = true;
    th.appendChild(btn);
    row.appendChild(th);
  }
  return row;
}

function mountArch(doc: Document, root: HTMLElement, arch: ArchSpec): void {
  const panel = doc.createElement('div');
  panel.id = arch.panelId;
  panel.className = arch.panelClass;
  const table = doc.createElement('table');
  table.className = 'PerioTable table-striped table-bordered';
  const body = doc.createElement('tbody');
  body.appendChild(mountPdRow(doc, arch.above));
  body.appendChild(mountHeaderRow(doc, arch.above));
  body.appendChild(mountPdRow(doc, arch.below));
  table.appendChild(body);
  panel.appendChild(table);
  root.appendChild(panel);
}

/**
 * Miss/Present host behaviour (§62 `applyButton` / `missingModals` /
 * `missingTooth`), minus the host's index bugs. Header click toggles the
 * yellow selection; Miss/Present marks a classed header `[Mis]` (or
 * Presents a marked one) and opens Apply Procedure for an unclassed one —
 * which is exactly the modal the Dock avoids by classing the header first.
 */
class EpmsDemoMissHost {
  /** `missingToothNumber`: selected header ids in click order. */
  #selected: number[] = [];

  constructor(
    private readonly doc: Document,
    private readonly chart: HTMLElement,
  ) {}

  wire(missPresent: HTMLButtonElement): void {
    for (const header of this.#headers()) {
      header.addEventListener('click', () => {
        if (!header.disabled) this.applyButton(Number(header.id));
      });
    }
    missPresent.addEventListener('click', () => this.missingModals());
  }

  #headers(): HTMLButtonElement[] {
    return [...this.chart.querySelectorAll<HTMLButtonElement>('th > button[id]')];
  }

  #header(id: number): HTMLButtonElement | null {
    const el = this.doc.getElementById(String(id));
    return el instanceof HTMLButtonElement ? el : null;
  }

  #push(id: number): void {
    if (!this.#selected.includes(id)) this.#selected.push(id);
  }

  #drop(id: number): void {
    this.#selected = this.#selected.filter(other => other !== id);
  }

  /** @param id - Header id (tooth − 1) */
  applyButton(id: number): void {
    const header = this.#header(id);
    if (!header) return;
    const yellow = header.classList.contains('missingColor');
    const classed = header.classList.contains('tooth-missing');
    if (yellow && !classed) {
      this.#drop(id);
      header.classList.remove('missingColor');
      return;
    }
    this.#push(id);
    header.classList.add('missingColor');
  }

  missingModals(): void {
    const unclassed = this.#selected.some(
      id => !this.#header(id)?.classList.contains('tooth-missing'),
    );
    if (unclassed) {
      this.#openApplyProcedure();
      return;
    }
    this.missingTooth();
  }

  /** `missingTooth('miss')`: mark when the PD cell is unpainted, else Present. */
  missingTooth(): void {
    for (const id of [...this.#selected]) {
      const tooth = id + 1;
      const cell = this.doc.getElementById(addressEpmsPdCell(tooth, 'MB')?.cellId ?? '');
      if (cell?.classList.contains('tooth-missing')) this.#present(tooth);
      else this.#mark(tooth);
    }
    this.#selected = [];
  }

  /** Like the host's `missingToothApply`: a [Mis] header is disabled too. */
  #mark(tooth: number): void {
    const header = this.#header(tooth - 1);
    if (header) {
      header.textContent = '[Mis]';
      header.classList.add('tooth-missing');
      header.classList.remove('missingColor');
      header.disabled = true;
    }
    for (const id of listEpmsToothColumnCellIds(tooth)) {
      const cell = this.doc.getElementById(id);
      if (!cell) continue;
      cell.classList.add('tooth-missing');
      for (const input of cell.querySelectorAll('input')) {
        input.disabled = true;
        input.value = '';
      }
    }
  }

  #present(tooth: number): void {
    const header = this.#header(tooth - 1);
    if (header) {
      header.textContent = String(tooth);
      header.classList.remove('tooth-missing', 'missingColor');
      header.disabled = false;
    }
    for (const id of listEpmsToothColumnCellIds(tooth)) {
      const cell = this.doc.getElementById(id);
      if (!cell) continue;
      cell.classList.remove('tooth-missing');
      for (const input of cell.querySelectorAll('input')) input.disabled = false;
    }
  }

  /** PrimeNG-style Apply Procedure: Cancel only — the Dock never clicks Add. */
  #openApplyProcedure(): void {
    if (this.doc.querySelector('[data-epms-apply-procedure]')) return;
    const dialog = this.doc.createElement('div');
    dialog.setAttribute('role', 'dialog');
    dialog.className = 'p-dialog p-component p-dialog-visible';
    dialog.setAttribute('data-epms-apply-procedure', '1');
    dialog.innerHTML =
      '<div class="p-dialog-header"><span>Apply Procedure</span></div>' +
      '<div class="p-dialog-content"><p>A tooth marked missing by hand needs a ' +
      '<strong>Date of Service</strong> (MSTH). The Dock never opens this — it ' +
      'classes the header first (§60).</p>' +
      '<label>Date of Service <input type="text" placeholder="MM/DD/YYYY" /></label></div>' +
      '<div class="p-dialog-footer"><button type="button" data-epms-dialog="add">Add</button>' +
      '<button type="button" data-epms-dialog="cancel">Cancel</button></div>';
    dialog
      .querySelector('[data-epms-dialog="cancel"]')
      ?.addEventListener('click', () => dialog.remove());
    this.doc.body.appendChild(dialog);
  }
}

function setEditMode(doc: Document, chart: HTMLElement, on: boolean): void {
  for (const el of [...chart.querySelectorAll<HTMLElement>('.perio-chart-input[id]')]) {
    const isInput = el instanceof HTMLInputElement;
    if (isInput === on) continue;
    const text = isInput ? el.value : (el.textContent ?? '');
    const next = mountValue(doc, el.id, el.getAttribute('title') ?? '', on, text);
    if (el.closest('td')?.classList.contains('tooth-missing') && next instanceof HTMLInputElement) {
      next.disabled = true;
    }
    el.replaceWith(next);
  }
  for (const header of chart.querySelectorAll<HTMLButtonElement>('th > button[id]')) {
    header.disabled = !on || header.classList.contains('tooth-missing');
  }
  chart.dataset.epmsEdit = on ? '1' : '0';
}

function mountControls(doc: Document): {
  bar: HTMLElement;
  edit: HTMLButtonElement;
  view: HTMLButtonElement;
  delay: HTMLInputElement;
  missPresent: HTMLButtonElement;
} {
  const bar = doc.createElement('div');
  bar.className = 'epms-chart-bar';
  bar.innerHTML =
    '<button type="button" data-epms-edit>Edit</button>' +
    '<button type="button" data-epms-view hidden>Back to view</button>' +
    '<label>Edit delay (ms) <input type="number" min="0" step="100" data-epms-edit-delay /></label>' +
    '<button type="button" data-epms-miss-present disabled>Miss/Present Tooth</button>' +
    '<span class="epms-mode" data-epms-mode>view</span>';
  const edit = bar.querySelector<HTMLButtonElement>('[data-epms-edit]')!;
  const view = bar.querySelector<HTMLButtonElement>('[data-epms-view]')!;
  const delay = bar.querySelector<HTMLInputElement>('[data-epms-edit-delay]')!;
  const missPresent = bar.querySelector<HTMLButtonElement>('[data-epms-miss-present]')!;
  delay.value = String(DEFAULT_EDIT_DELAY_MS);
  return { bar, edit, view, delay, missPresent };
}

/**
 * Builds `<app-clinical-perio>` → controls + `<app-perio-tooth-chart>` with
 * both arch panels, in view mode (spans, disabled headers), like a freshly
 * opened EPMS chart.
 *
 * @param doc - Target document
 */
export function mountEpmsFixtureGrid(doc: Document): void {
  let root = doc.getElementById('epms-perio-grid');
  if (!root) {
    root = doc.createElement('div');
    root.id = 'epms-perio-grid';
    doc.body.appendChild(root);
  }
  root.replaceChildren();
  doc.querySelector('[data-epms-apply-procedure]')?.remove();

  const clinical = doc.createElement('app-clinical-perio');
  const controls = mountControls(doc);
  const chart = doc.createElement('app-perio-tooth-chart');
  for (const arch of ARCHES) mountArch(doc, chart, arch);
  clinical.append(controls.bar, chart);
  root.appendChild(clinical);

  const host = new EpmsDemoMissHost(doc, chart);
  host.wire(controls.missPresent);

  const mode = controls.bar.querySelector<HTMLElement>('[data-epms-mode]')!;
  const enterEdit = (): void => {
    setEditMode(doc, chart, true);
    controls.edit.hidden = true;
    controls.edit.disabled = false;
    controls.edit.textContent = 'Edit';
    controls.view.hidden = false;
    controls.missPresent.disabled = false;
    mode.textContent = 'edit';
  };
  controls.edit.addEventListener('click', () => {
    const wait = Math.max(0, Number(controls.delay.value) || 0);
    controls.edit.disabled = true;
    controls.edit.textContent = `Opening… (${wait} ms)`;
    mode.textContent = 'entering edit';
    doc.defaultView?.setTimeout(enterEdit, wait);
  });
  controls.view.addEventListener('click', () => {
    setEditMode(doc, chart, false);
    controls.view.hidden = true;
    controls.edit.hidden = false;
    controls.missPresent.disabled = true;
    mode.textContent = 'view';
  });
}

/**
 * Simulates the ~30s EPMS section remount: wipe and rebuild the same ids
 * (back in view mode, like the real chart). The Edit delay is the tester's
 * dial, not chart state, so it survives the remount.
 *
 * @param doc - Demo document
 */
export function remountEpmsChart(doc: Document): void {
  const delay = doc.querySelector<HTMLInputElement>('[data-epms-edit-delay]')?.value;
  mountEpmsFixtureGrid(doc);
  const input = doc.querySelector<HTMLInputElement>('[data-epms-edit-delay]');
  if (input && delay !== undefined) input.value = delay;
}

/**
 * Mounts the fixture chart. Sim buttons are wired by the Dock EPMS adapter.
 *
 * @param doc - Demo document
 */
export function wireEpmsDemoPage(doc: Document): void {
  mountEpmsFixtureGrid(doc);
  const remount = doc.querySelector('[data-epms-remount]');
  remount?.addEventListener('click', () => remountEpmsChart(doc));
  const select = doc.getElementById('patient-select') as HTMLSelectElement | null;
  const win = doc.defaultView;
  select?.addEventListener('change', () => {
    const id = select.value;
    if (win && id) win.location.hash = `#/clinical/perio/demo/${id}`;
  });
}
